var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { OGPanel } from '@opengear/panel';
import AddIcon from '@mui/icons-material/AddCircleOutlineSharp';
import { OGButtonLink } from '@opengear/buttons';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import FormHelperText from '@mui/material/FormHelperText';
import { OGIconTooltip } from '@opengear/icon-tooltip';
import { useUpdateHostname } from '@/api/system/hostname/hostname';
import { useUpdateHttpsPort } from '@/api/system/https-port';
import { useUpdateSshPort } from '@/api/system/ssh-port';
import { useFormSync } from '@/hooks/use-form-sync';
import { HostnameField, HttpsPortField, SshPortField } from './network-settings-form-controls';
import { ExternalNetworkTable } from './external-network/external-network-table';
import { FormActions, FormContainer, ResetButton, SubmitButton } from '@/components/layout/forms';
import { useUpdateExternalEndpoints } from '@/api/system/external-endpoints';
import { transformFormStateToRequest } from './transformers';
import { useOpen } from '@/hooks/open';
import { CreateExternalNetworkModal, UpdateExternalNetworkModal } from './external-network';
export var NETWORK_SETTINGS_FORM_LOADER_ID = 'NetworkSettings-form-loader';
export function NetworkSettingsForm(_a) {
    var _this = this;
    var _b, _c;
    var data = _a.data, isFetching = _a.isFetching, permissions = _a.permissions;
    var updateHostname = useUpdateHostname();
    var updateHttpsPort = useUpdateHttpsPort().mutate;
    var updateSshPort = useUpdateSshPort();
    var updateSystemExternalEndpoints = useUpdateExternalEndpoints();
    var t = useTranslation().t;
    var methods = useForm({ mode: 'onTouched', defaultValues: data });
    var _d = methods.formState, dirtyFields = _d.dirtyFields, defaultValues = _d.defaultValues, errors = _d.errors;
    var reset = methods.reset;
    useFormSync(defaultValues, data, methods.reset, t('pages.network_settings.form.updated'), isFetching);
    var endpointsWatch = methods.watch('endpoints');
    var endpointsError = (_c = (_b = errors.endpoints) === null || _b === void 0 ? void 0 : _b.root) === null || _c === void 0 ? void 0 : _c.message;
    var onSubmit = useCallback(function (formData) { return __awaiter(_this, void 0, void 0, function () {
        var _a, hostname, httpsPort, sshPort, endpoints, promises;
        var _this = this;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = transformFormStateToRequest(formData), hostname = _a.system_hostname, httpsPort = _a.system_https_port, sshPort = _a.system_ssh_port, endpoints = _a.system_external_endpoints;
                    promises = [];
                    if (dirtyFields.hostname) {
                        promises.push((function () { return __awaiter(_this, void 0, void 0, function () {
                            var updatedData;
                            var _a;
                            return __generator(this, function (_b) {
                                switch (_b.label) {
                                    case 0: return [4 /*yield*/, updateHostname(hostname)];
                                    case 1:
                                        updatedData = _b.sent();
                                        if (updatedData) {
                                            methods.resetField('hostname', { defaultValue: (_a = updatedData.system_hostname.hostname.toString()) !== null && _a !== void 0 ? _a : '' });
                                        }
                                        return [2 /*return*/];
                                }
                            });
                        }); })());
                    }
                    if (dirtyFields.httpsPort) {
                        promises.push((function () { return __awaiter(_this, void 0, void 0, function () {
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, updateHttpsPort({ system_https_port: httpsPort }, {
                                            onSuccess: function (updatedData) {
                                                var _a;
                                                if (updatedData)
                                                    methods.resetField('httpsPort', { defaultValue: (_a = updatedData.system_https_port.port) !== null && _a !== void 0 ? _a : '' });
                                            },
                                        })];
                                    case 1:
                                        _a.sent();
                                        return [2 /*return*/];
                                }
                            });
                        }); })());
                    }
                    if (dirtyFields.sshPort) {
                        promises.push((function () { return __awaiter(_this, void 0, void 0, function () {
                            var updatedData;
                            var _a;
                            return __generator(this, function (_b) {
                                switch (_b.label) {
                                    case 0: return [4 /*yield*/, updateSshPort(sshPort)];
                                    case 1:
                                        updatedData = _b.sent();
                                        if (updatedData) {
                                            methods.resetField('sshPort', { defaultValue: (_a = updatedData.system_ssh_port.port) !== null && _a !== void 0 ? _a : '' });
                                        }
                                        return [2 /*return*/];
                                }
                            });
                        }); })());
                    }
                    if (dirtyFields.endpoints) {
                        promises.push((function () { return __awaiter(_this, void 0, void 0, function () {
                            var updatedData, updatedFormData;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, updateSystemExternalEndpoints({ system_external_endpoints: endpoints })];
                                    case 1:
                                        updatedData = _a.sent();
                                        if (updatedData) {
                                            updatedFormData = __assign({}, formData);
                                            updatedFormData.endpoints = updatedData.system_external_endpoints;
                                            // resetField doesn't appear to work correctly with field-arrays, so we have to reset the entire form
                                            reset(updatedFormData);
                                        }
                                        return [2 /*return*/];
                                }
                            });
                        }); })());
                    }
                    return [4 /*yield*/, Promise.all(promises)];
                case 1:
                    _b.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [
        dirtyFields.endpoints,
        dirtyFields.hostname,
        dirtyFields.httpsPort,
        dirtyFields.sshPort,
        methods,
        reset,
        updateHostname,
        updateHttpsPort,
        updateSshPort,
        updateSystemExternalEndpoints,
    ]);
    var _e = __read(useState(null), 2), editAddress = _e[0], setEditAddress = _e[1];
    var createNetworkOpen = useOpen();
    var updateNetworkOpen = useOpen();
    var _f = useFieldArray({ name: 'endpoints', control: methods.control, keyName: 'fieldId' }), fields = _f.fields, append = _f.append, remove = _f.remove, update = _f.update;
    var maxExternalNetworksAdded = fields.length >= 5;
    var externalNetworkUpdateIndex = useMemo(function () {
        if (!editAddress)
            return -1;
        return endpointsWatch.findIndex(function (i) { return i.id === editAddress.id; });
    }, [editAddress, endpointsWatch]);
    var handleExternalNetworkCreate = useCallback(function (formData) {
        append(formData);
        createNetworkOpen.handleClose();
    }, [append, createNetworkOpen]);
    var handleExternalNetworkUpdate = useCallback(function (formData) {
        update(externalNetworkUpdateIndex, formData);
        updateNetworkOpen.handleClose();
    }, [updateNetworkOpen, update, externalNetworkUpdateIndex]);
    var handleExternalNetworkDelete = useCallback(function () {
        remove(externalNetworkUpdateIndex);
        updateNetworkOpen.handleClose();
    }, [remove, externalNetworkUpdateIndex, updateNetworkOpen]);
    var handleAddressClick = useCallback(function (address) {
        setEditAddress(address);
        updateNetworkOpen.handleOpen();
    }, [updateNetworkOpen]);
    return (_jsxs(OGPanel, { children: [_jsxs(FormContainer, __assign({ mode: "update", methods: methods, permissions: permissions, onSubmit: onSubmit }, { children: [_jsx(HostnameField, {}), _jsxs(Typography, __assign({ component: "h3", variant: "h3", marginBottom: 1, marginTop: 2 }, { children: [t('pages.network_settings.direct_access_port.heading'), _jsx(OGIconTooltip, { title: t('pages.network_settings.direct_access_port.tooltip') })] })), _jsx(HttpsPortField, {}), _jsx(SshPortField, {}), _jsx(Typography, __assign({ component: "h3", variant: "h3", marginBottom: 1, marginTop: 2 }, { children: t('pages.network_settings.external_address.table.label') })), _jsxs(Typography, __assign({ component: "h5", variant: "h5", gutterBottom: true, textTransform: "none" }, { children: [t('pages.network_settings.external_address.table.subheading'), _jsx(OGIconTooltip, { title: t('pages.network_settings.external_address.table.tooltip') })] })), _jsx(ExternalNetworkTable, { onRowClick: handleAddressClick }), endpointsError && (_jsxs(FormHelperText, __assign({ sx: { marginTop: 0, marginBottom: 2, display: 'flex', alignItems: 'center' }, error: true }, { children: [_jsx(ErrorIcon, { fontSize: "small", color: "error", sx: { marginRight: 0.5 } }), endpointsError] }))), _jsx(Box, __assign({ sx: { marginTop: 2, width: 'fit-content' } }, { children: _jsx(Tooltip, __assign({ arrow: true, placement: "top", title: maxExternalNetworksAdded ? t('pages.network_settings.external_address.form.maxNetworks') : '' }, { children: _jsx("span", { children: _jsx(OGButtonLink, __assign({ startIcon: _jsx(AddIcon, {}), onClick: createNetworkOpen.handleOpen, disabled: !permissions.hasEditPermission || maxExternalNetworksAdded }, { children: t('pages.network_settings.external_address.modal.headers.create') })) }) })) })), _jsxs(FormActions, { children: [_jsx(ResetButton, {}), _jsx(SubmitButton, {})] })] })), _jsx(CreateExternalNetworkModal, { useOpen: createNetworkOpen, permissions: permissions, onSubmit: handleExternalNetworkCreate }), editAddress && (_jsx(UpdateExternalNetworkModal, { useOpen: updateNetworkOpen, permissions: permissions, onSubmit: handleExternalNetworkUpdate, onDelete: handleExternalNetworkDelete, defaultValues: editAddress }))] }));
}
